import { useAppSelector } from 'app/hooks';
import {
  BasicProductData,
  EnrichedAccessory
} from 'components/layout/modals/compatible-combinations-browser-modal';
import { DuctedComponentSwapHandler } from 'hooks/use-ducted-component-swap';
import {
  AccessoryPick,
  AccessoryPicks,
  groupCombinationAccessories,
  hasAccessoryChoice
} from 'utils/ducted-accessory-groups';
import { getExistingEquipmentTypeLabel } from 'utils/existing-equipment';
import DuctedAccessoryRows from './ducted-accessory-rows';
import DuctedSlotProductRow from './ducted-slot-product-row';
import { ductedSlotRows } from './ducted-slot-rows';
import DuctedSwappableSlotRows from './ducted-swappable-slot-rows';

// System Components table for the ducted detail panel (AHA-6681 CS-3).
// A new table rather than SystemComponentsTable: that one is hard-wired to
// configuration.condensers + the useEditableSystemMap swap machinery, none
// of which exists for a compatible-combination row. Rows follow the CCB
// ModelSpecsTooltip body conventions (photo, name, price/Sale, stock,
// product link); with a swap handler each real slot also carries the same
// swap/lock controls as the comparison column, resolved against the rail
// section's sibling combinations.
type DuctedComponentsTableProps = Readonly<{
  combination: Record<string, unknown>;
  productData: Record<string, BasicProductData>;
  accessoryData: Record<string, EnrichedAccessory>;
  systemUniqueId: string;
  accessoryPicks: AccessoryPicks;
  onAccessoryPick: (accessoryTypeCode: string, pick: AccessoryPick) => void;
  accessoryPicksDisabled?: boolean;
  onComponentSwap?: DuctedComponentSwapHandler;
}>;

export default function DuctedComponentsTable({
  combination,
  productData,
  accessoryData,
  systemUniqueId,
  accessoryPicks,
  onAccessoryPick,
  accessoryPicksDisabled = false,
  onComponentSwap
}: DuctedComponentsTableProps) {
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );

  // One row per real slot (non-Generic, non-null model). Generic / kept
  // slots have no productData row and render no product row here — they
  // still appear in the header config lines as the customer's unit.
  const slotRows = ductedSlotRows(combination, productData);

  // Accessory pairs absent from accessoryData have no AlpineProductLink and
  // aren't quotable — skipped silently (CCB convention).
  const accessoryGroups = groupCombinationAccessories(
    combination.Accessories as string | null | undefined,
    accessoryData
  );
  const showPicksLockedHint =
    accessoryPicksDisabled && accessoryGroups.some(hasAccessoryChoice);

  // Kept annotation: combination rows conflate kept anchors with Generic
  // placeholders (see the 'none' comment in utils/combination-pricing), so
  // the honest source is the selected existing system's !replacing
  // components.
  const keptComponents =
    existingSystems
      .find((system) => system.uniqueId === systemUniqueId)
      ?.components.filter((component) => !component.replacing) ?? [];

  return (
    <div className='flex flex-col gap-3'>
      {slotRows.length > 0 && (
        <div className='divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200'>
          {onComponentSwap ? (
            <DuctedSwappableSlotRows
              key={systemUniqueId}
              rows={slotRows}
              combination={combination}
              systemUniqueId={systemUniqueId}
              onComponentSwap={onComponentSwap}
            />
          ) : (
            slotRows.map((row) => (
              <DuctedSlotProductRow key={row.slot} row={row} />
            ))
          )}
        </div>
      )}
      {accessoryGroups.length > 0 && (
        <div>
          <div className='mb-1 flex flex-wrap items-baseline justify-between gap-x-3'>
            <div className='text-xs font-semibold uppercase tracking-wider text-gray-600'>
              Accessories
            </div>
            {showPicksLockedHint && (
              <div className='text-[11px] text-gray-400'>
                Remove this system from the comparison to change accessories.
              </div>
            )}
          </div>
          <DuctedAccessoryRows
            groups={accessoryGroups}
            picks={accessoryPicks}
            onPick={onAccessoryPick}
            radioName={`ducted-accessory-${String(combination.ID)}`}
            disabled={accessoryPicksDisabled}
          />
        </div>
      )}
      {keptComponents.length > 0 && (
        <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
          <div className='text-xs font-semibold uppercase tracking-wider text-gray-600'>
            Keeping your existing equipment
          </div>
          <ul className='mt-1 space-y-0.5 text-xs text-gray-700'>
            {keptComponents.map((component) => (
              <li key={component.uniqueId}>
                {getExistingEquipmentTypeLabel(component)}
                {component.modelNumber && ` — ${component.modelNumber}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
